/**
 * The signature element: one arc carrying both figures.
 *
 * The vendor's number and the computed number sit on the same track so the difference
 * reads as a visible wedge rather than as two statistics the viewer has to subtract.
 * When there is no computed figure the wedge is absent and the dial says so, because a
 * dial that renders the vendor's claim alone would imply verification that never
 * happened.
 */

import { NOT_MEASURED } from "../lib/format";

interface Props {
  reported: number | null;
  computed: number | null;
  size?: number;
  legend?: boolean;
  label?: string;
}

// Angles run clockwise from the top: 0 is 12 o'clock, 90 is 3 o'clock. Starting at
// 235 and sweeping 250 leaves the opening at the bottom, where a gauge's opening
// belongs, rather than cutting the arc at one side.
const SWEEP = 250;
const START = 235;

function polar(cx: number, cy: number, radius: number, degrees: number) {
  const radians = ((degrees - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function arcPath(cx: number, cy: number, radius: number, from: number, to: number) {
  const start = polar(cx, cy, radius, from);
  const end = polar(cx, cy, radius, to);
  const large = to - from > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${large} 1 ${end.x} ${end.y}`;
}

export function LedgerDial({ reported, computed, size = 128, legend = true, label }: Props) {
  const radius = size / 2 - 12;
  const centre = size / 2;
  const toAngle = (value: number) => START + (Math.min(Math.max(value, 0), 100) / 100) * SWEEP;

  const hasBoth = reported !== null && computed !== null;
  const gap = hasBoth ? Number((reported - computed).toFixed(1)) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <svg width={size} height={size * 0.82} viewBox={`0 0 ${size} ${size * 0.82}`} role="img"
           aria-label={
             hasBoth
               ? `Vendor reported ${reported}, independently computed ${computed}`
               : `Independently computed ${computed ?? NOT_MEASURED}`
           }>
        <path
          d={arcPath(centre, centre - 6, radius, START, START + SWEEP)}
          fill="none"
          stroke="rgba(239,233,221,0.12)"
          strokeWidth={9}
          strokeLinecap="round"
        />
        {computed !== null && (
          <path
            d={arcPath(centre, centre - 6, radius, START, toAngle(computed))}
            fill="none"
            stroke="var(--gold)"
            strokeWidth={9}
            strokeLinecap="round"
          />
        )}
        {hasBoth && reported > computed && (
          <path
            d={arcPath(centre, centre - 6, radius, toAngle(computed), toAngle(reported))}
            fill="none"
            stroke="var(--rust)"
            strokeWidth={9}
            strokeLinecap="butt"
            opacity={0.85}
          />
        )}
        <text
          x={centre}
          y={centre - 2}
          textAnchor="middle"
          className="serif num"
          fill="var(--gold)"
          fontSize={size * 0.26}
        >
          {computed ?? "—"}
        </text>
        {label && (
          <text
            x={centre}
            y={centre + size * 0.14}
            textAnchor="middle"
            fill="var(--slate)"
            fontSize={10}
            letterSpacing="0.06em"
          >
            {label.toUpperCase()}
          </text>
        )}
      </svg>
      {legend && (
        <div style={{ fontSize: 11, color: "var(--slate)", textAlign: "center", lineHeight: 1.5 }}>
          {hasBoth ? (
            <>
              <div>
                Vendor reported <span className="num" style={{ color: "var(--parchment)" }}>{reported}</span>
              </div>
              <div style={{ color: gap && gap > 0 ? "var(--rust)" : "var(--green)" }}>
                {gap && gap > 0 ? `Overstated by ${gap} pts` : "No overstatement"}
              </div>
            </>
          ) : (
            <div>{NOT_MEASURED} independently</div>
          )}
        </div>
      )}
    </div>
  );
}
